/* eslint-disable import/no-duplicates */
import {
    CellValueChangedEvent,
    ColDef,
    ColGroupDef,
    GridApi,
    GridReadyEvent,
    PasteEndEvent,
    RowDragEndEvent,
    RowDragEnterEvent,
} from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { AgGridReact } from "ag-grid-react";
import { $, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import cloneDeep from "lodash/cloneDeep";
import lodashGet from "lodash/get";
import groupBy from "lodash/groupBy";
import lodashSet from "lodash/set";
import FlatButton from "material-ui/FlatButton";
import * as React from "react";
import { DoorGroupsSelectCellEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/dropdown-door-groups-editor/door-groups-select-cell-editor";
import { NumberCellEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/number-cell-editor";
import { TextEditor } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/text-editor";
import { CustomDoorGroupAttributeHeaderComponent } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-headers/custom-door-group-attribute-header";
import { DefaultRendererVanilla } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/default-renderer-vanilla";
import { DimentionActionCommands } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/dimension-types";
import { DATATEST } from "constants/integration";
import { Editors, Kinds, UnitsEntryType, ValueTypes } from "constants/product-collection";
import {
    AllocationDimensionValueFields,
    getItemsToRemoveOldValuesInAllowedValue,
    getUserDefinedClassificationName,
    IAllocationDimension,
    IAllocationDimensionValue,
    ICreateAllocationDimensionValue,
    IUpdateAllocationDimensionValue,
} from "helpers/allocation-dimension";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { NUMBER_OF_DOORS, PURCHASE_ORDER_DOOR_GROUP_FIELD } from "helpers/door-collection";
import { translateCellBooleanInput } from "helpers/objUtils";
import { defaultValueGetter, getValueByKey, getValueGetter, ISchemaKey } from "helpers/product-collection";
import { isValidNumber } from "lib/number";
import { theme } from "styles/theme";
import { YesNoCellRenderer, yesNoValueFormatter } from "./yes-no-cell-renderer";
import { IStore } from "reducers";
import { connect } from "react-redux";
import { ExportMode } from "components/product-collection/manage-allocation-dimension/constants";

const GRID_COMPONENTS = {
    numberCellEditor: NumberCellEditor,
    textEditor: TextEditor,
    yesNoCellRenderer: YesNoCellRenderer,
    doorGroupsSelectCellEditor: DoorGroupsSelectCellEditor,
};

const GRID_COMPONENTS_VANILLA = {
    defaultRenderer: DefaultRendererVanilla,
};

const getRowNodeId = (data) => {
    return data.id;
};

const NEW_ROW_ID = "_create";
const newRowData: IAllocationDimensionValue = {
    id: NEW_ROW_ID,
    external_id: "",
    name: "",
    active: true,
    included: true,
    fields: {},
};

const styles = merge(
    {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    $(" .allocationDimensionGrid", {
        width: "100%",
        height: "450px",
        margin: "0",
    }),
    $(" .ag-status-bar", {
        display: "none",
    })
);

const largeGridStyles = merge(
    {
        width: "100%",
        display: "flex",
        flexDirection: "column",
    },
    $(" .allocationDimensionGrid", {
        width: "100%",
        height: "70vh",
        margin: "0",
    }),
    $(" .ag-status-bar", {
        display: "none",
    }),
    $(" .allocationDimensionAttribute--notEditable", {
        color: "#a5a0a0",
    })
);

const getContextMenuItems = () => ["copy", "separator", "export"];

const commonColDefProps = {
    lockPosition: true,
    suppressMovable: true,
    floatingFilter: true,
    suppressMenu: true,
    resizable: true,
    filter: true,
    sortable: true,
};

const defaultValueSetter = ({ colDef, newValue, data }) => {
    const type = typeof newValue === "boolean" ? "bool_value" : "string_value";
    const options = lodashGet(colDef, "cellEditorParams.values", []);
    const value = options.length
        ? options.find((it) => it?.toLocaleLowerCase() === newValue?.toLocaleLowerCase().trim()) || ""
        : newValue;

    lodashSet(data, ["fields", colDef.field, type], value);
    return true;
};

const editableSelectValueSetter = ({ colDef, newValue, data }) => {
    const type = typeof newValue === "boolean" ? "bool_value" : "string_value";
    const options = lodashGet(colDef, "cellEditorParams.values", []);
    const value = options.find((it) => it?.toLocaleLowerCase() === newValue?.toLocaleLowerCase().trim()) ?? newValue;

    lodashSet(data, ["fields", colDef.field, type], value);
    return true;
};

const booleanValueSetter = ({ colDef, newValue, data }) => {
    const cellValue = translateCellBooleanInput(newValue);
    lodashSet(data, [colDef.field], cellValue);
    return true;
};

const numberValueSetter = ({ colDef, newValue, data }) => {
    if (isValidNumber(newValue) || !newValue) {
        lodashSet(data, ["fields", colDef.field, "number_value"], !newValue ? undefined : newValue);
        return true;
    }
    return false;
};

// We can make this reusable if it supports more case.
// Now it only handles schema that has "string default_kind" or "allowed_values"
export const buildAttributeColDef = (
    schema: ISchemaKey,
    options: { readOnly?: boolean; purchaseOrderDoorGroups: IPurchaseOrderDoorGroup[]; collectionId?: string },
    onAttributeAction: (key: string, isDeleteMode?: boolean) => void
): ColDef | null => {
    const handleDoorAttributesAction = (command: DimentionActionCommands) => {
        switch (command) {
            case DimentionActionCommands.RenameDoorGrouping:
                onAttributeAction(schema.field_name);
                break;
            case DimentionActionCommands.DeleteDoorGrouping:
                onAttributeAction(schema.field_name, true);
                break;
        }
    };
    const { readOnly, purchaseOrderDoorGroups } = options;
    if (schema.field_name === PURCHASE_ORDER_DOOR_GROUP_FIELD) {
        return purchaseOrderDoorGroups && purchaseOrderDoorGroups.length > 0
            ? {
                  field: schema.field_name,
                  headerName: schema.display_name,
                  editable: !readOnly,
                  cellEditor: Editors.AgRichSelectCellEditor,
                  cellEditorPopup: true,
                  cellEditorParams: {
                      freeform: false,
                      values: [""].concat(
                          purchaseOrderDoorGroups.map((purchaseOrderDoorGroup) => purchaseOrderDoorGroup.id)
                      ),
                  },
                  valueSetter: defaultValueSetter,
                  valueGetter: getValueGetter(schema),
                  valueFormatter: ({ value }) => {
                      return purchaseOrderDoorGroups.find(
                          (purchaseOrderDoorGroup) => purchaseOrderDoorGroup.id === value
                      )?.name;
                  },
              }
            : {
                  field: schema.field_name,
                  headerName: schema.display_name,
                  editable: false,
                  valueGetter: getValueGetter(schema),
              };
    } else if (schema.display?.is_user_defined) {
        const field = options.collectionId
            ? getUserDefinedClassificationName(schema.field_name, options.collectionId)
            : schema.field_name;
        return {
            field,
            headerName: schema.display_name,
            headerTooltip: schema.display_name,
            headerComponent: CustomDoorGroupAttributeHeaderComponent,
            headerComponentParams: {
                onActionInvoked: handleDoorAttributesAction,
                readOnly,
            },
            editable: !readOnly,
            cellEditor: Editors.DoorGroupsSelectCellEditor,
            cellEditorPopup: true,
            cellEditorParams: {
                values: mapCellEditorParams(schema.constraint?.allowed_values),
                fieldName: field,
            },
            valueSetter: editableSelectValueSetter,
            valueGetter: getValueGetter(schema),
            minWidth: 168,
        };
    } else if (schema.constraint) {
        let values = [];
        if (schema.constraint.allowed_values && schema.constraint.allowed_values.length) {
            values = schema.constraint.allowed_values.map((val) => val.string_value);
        }

        return {
            field: schema.field_name,
            headerName: schema.display_name,
            editable: !readOnly && !schema.display?.read_only,
            cellEditor: Editors.AgRichSelectCellEditor,
            cellEditorPopup: true,
            cellEditorParams: {
                freeform: schema.constraint.freeform,
                values,
            },
            cellClass: !readOnly && !schema.display?.read_only ? "" : "allocationDimensionAttribute--notEditable",
            valueSetter: defaultValueSetter,
            valueGetter: getValueGetter(schema),
        };
    } else if (schema.value_type === ValueTypes.NUMBER) {
        return {
            field: schema.field_name,
            headerName: schema.display_name,
            cellEditor: Editors.NumberCellEditor,
            editable: !readOnly,
            valueSetter: ({ newValue, ...rest }) =>
                schema.field_name === NUMBER_OF_DOORS
                    ? numberValueSetter({ ...rest, newValue: newValue })
                    : defaultValueSetter({ ...rest, newValue: String(newValue) }),

            valueGetter:
                schema.field_name === NUMBER_OF_DOORS
                    ? ({ data }) => getValueByKey(data, schema.field_name, "number_value")
                    : defaultValueGetter.bind(this, schema), // numberValueGetter rounds the value
        };
    } else if (
        (schema.kind === Kinds.DEFAULT_KIND || schema.kind === Kinds.ALLOCATION_DIMENSION_CLASSIFICATION) &&
        schema.value_type === ValueTypes.STRING
    ) {
        return {
            field: schema.field_name,
            headerName: schema.display_name,
            editable: !readOnly,
            valueSetter: defaultValueSetter,
            valueGetter: getValueGetter(schema),
        };
    }
    return null;
};

export const getCommonAllocationDimensionColDefs = (readOnly, uneditableColumns = []): ColDef[] => [
    {
        field: AllocationDimensionValueFields.ExternalId,
        headerName: formatMessage("Code/ID"),
        editable: !readOnly && !uneditableColumns.includes(AllocationDimensionValueFields.ExternalId),
    },
    {
        field: AllocationDimensionValueFields.Name,
        headerName: formatMessage("Name"),
        editable: !readOnly && !uneditableColumns.includes(AllocationDimensionValueFields.Name),
    },
    {
        field: AllocationDimensionValueFields.Active,
        headerName: formatMessage("Active"),
        editable: !readOnly && !uneditableColumns.includes(AllocationDimensionValueFields.Active),
        cellEditor: "agRichSelectCellEditor",
        cellEditorParams: {
            values: [true, false],
            formatValue: yesNoValueFormatter,
        },
        cellRenderer: "yesNoCellRenderer",
        valueSetter: booleanValueSetter,
        filterValueGetter: ({ data }) => yesNoValueFormatter(data[AllocationDimensionValueFields.Active]),
    },
    {
        field: AllocationDimensionValueFields.Included,
        headerName: formatMessage("In Assortment"),
        editable: !readOnly && !uneditableColumns.includes(AllocationDimensionValueFields.Included),
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
            values: [true, false],
            formatValue: yesNoValueFormatter,
        },
        cellRenderer: "yesNoCellRenderer",
        valueSetter: booleanValueSetter,
        filterValueGetter: ({ data }) => yesNoValueFormatter(data[AllocationDimensionValueFields.Included]),
    },
];

const getRowDataWithIndex = (api: GridApi) => {
    if (!api) {
        return [];
    }

    const data = [];
    api.forEachNode((it) =>
        data.push({
            data: it.data,
            rowIndex: it.rowIndex,
        })
    );
    return data;
};

type ReplaceAllowedValueByIndexProps = {
    doorAttribute: ISchemaKey;
    allowedValues: Array<{ string_value: string }>;
    indexOfDoorAttribute: number;
    allocationDimensionAttributes: ISchemaKey[];
};

export const replaceAllowedValueByIndex = ({
    doorAttribute,
    allowedValues,
    indexOfDoorAttribute,
    allocationDimensionAttributes,
}: ReplaceAllowedValueByIndexProps) => {
    const newAllocationDimensionAttributes = [...allocationDimensionAttributes];
    const freeform = doorAttribute.constraint?.freeform;

    const newDoorAttribute = {
        ...doorAttribute,
        constraint: {
            allowed_values: allowedValues,
            freeform: freeform ?? false,
        },
    };

    // replace at right index to avoid displacing the position in the UI.
    newAllocationDimensionAttributes.splice(indexOfDoorAttribute, 1, newDoorAttribute);

    return newAllocationDimensionAttributes;
};

type RemoveOldValueFromAllowedValueProps = {
    allocationDimensionAttributes: ISchemaKey[];
    valuesToRemove: string[];
    fieldName: string;
};

export const removeOldValueFromAllowedValue = ({
    allocationDimensionAttributes,
    valuesToRemove,
    fieldName,
}: RemoveOldValueFromAllowedValueProps): ISchemaKey[] | undefined => {
    const targetAttributeIndex = allocationDimensionAttributes.findIndex(
        (attribute: ISchemaKey) => attribute.field_name === fieldName
    );

    if (targetAttributeIndex === -1) {
        return;
    }

    const allocationDimensionAttributesClone = [...allocationDimensionAttributes];
    const { allowed_values: targetAllowedValues = [] } =
        allocationDimensionAttributesClone[targetAttributeIndex].constraint;

    const filteredAllowedValues = targetAllowedValues.filter(
        ({ string_value }) => !valuesToRemove.some((target) => target.toLowerCase() === string_value.toLowerCase())
    );

    allocationDimensionAttributesClone.splice(targetAttributeIndex, 1, {
        ...allocationDimensionAttributesClone[targetAttributeIndex],
        constraint: { allowed_values: filteredAllowedValues },
    });

    return allocationDimensionAttributesClone;
};

export type HandleCustomDoorAttributeCellChangedProps = {
    fieldNameToCustomValueList: {
        field: string;
        value: string;
        oldValue?: string;
        shouldDeleteOldValueFromAllowedValues?: boolean;
    }[];
    rowIndexToDataMap: Map<number, any>;
    isPasteOp: boolean;
    attributes: ISchemaKey[];
    allocationDimensionId: string;
    onCustomDoorCellValueChanged?: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
};

export enum KeyOfFieldNameToCustomValue {
    OldValue = "oldValue",
    ShouldDeleteOldValueFromAllowedValues = "shouldDeleteOldValueFromAllowedValues",
    Field = "field",
    Value = "value",
}

export const handleCustomDoorAttributeCellChanged = ({
    fieldNameToCustomValueList,
    rowIndexToDataMap,
    isPasteOp,
    attributes,
    allocationDimensionId,
    onCustomDoorCellValueChanged,
}: HandleCustomDoorAttributeCellChangedProps) => {
    let _attributesClone = [...attributes];

    const fieldValueListWithoutOldValues = fieldNameToCustomValueList.map((value) => ({
        field: value.field,
        value: value.value,
    }));

    const existAnyValueToCleanup = fieldNameToCustomValueList.some(
        (value) =>
            value.hasOwnProperty(KeyOfFieldNameToCustomValue.ShouldDeleteOldValueFromAllowedValues) &&
            value.shouldDeleteOldValueFromAllowedValues
    );

    if (existAnyValueToCleanup) {
        const itemsToRemoveOldValueInAllowedValue = getItemsToRemoveOldValuesInAllowedValue(fieldNameToCustomValueList);

        for (const field in itemsToRemoveOldValueInAllowedValue) {
            const allocationDimensionsWithNewAllowedValues = removeOldValueFromAllowedValue({
                allocationDimensionAttributes: _attributesClone,
                valuesToRemove: itemsToRemoveOldValueInAllowedValue[field],
                fieldName: field,
            });

            if (allocationDimensionsWithNewAllowedValues) {
                _attributesClone = allocationDimensionsWithNewAllowedValues;
            }
        }
    }

    const fieldNameToCustomValueMap = {};

    // The list contains fields with custom values. Create a map for each field with array of values.
    fieldValueListWithoutOldValues.forEach((item) => {
        if (fieldNameToCustomValueMap[item.field]) {
            if (!fieldNameToCustomValueMap[item.field].includes(item.value)) {
                fieldNameToCustomValueMap[item.field].push(item.value);
            }
        } else {
            fieldNameToCustomValueMap[item.field] = [item.value];
        }
    });

    for (const field in fieldNameToCustomValueMap) {
        const resultAttributes = checkIfCustomValueInAllowedValues(
            _attributesClone,
            field,
            fieldNameToCustomValueMap[field]
        );
        // will not be undefined if atleast one of the custom values is not present in existing values.
        // Proceed with the mutation of _attributesClone, otherwise skip.
        if (resultAttributes || !isPasteOp) {
            _attributesClone = resultAttributes;
        }
    }

    onCustomDoorCellValueChanged(_attributesClone, allocationDimensionId, rowIndexToDataMap);
    return _attributesClone;
};

const mapCellEditorParams = (schemaAllowedValues: ISchemaKey["constraint"]["allowed_values"] | undefined) => {
    if (!schemaAllowedValues) {
        return [];
    }
    // Generate an array with an empty string as the first value to allow user
    // to delete a value by selecting it
    return schemaAllowedValues.reduce(
        (acc: string[], { string_value }) => {
            if (string_value === "") {
                return acc;
            }
            return [...acc, string_value];
        },
        [""]
    );
};

/**
 * Use latest data from the grid to generate allowed values for certain attribute
 * @param attributes
 * @param attributeField
 * @param gridApi
 * @returns
 */
export const updateAllowedValues = (
    attributes: ISchemaKey[],
    attributeField: string,
    gridApi: GridApi
): ISchemaKey[] => {
    const updatedAttributes = cloneDeep(attributes);
    const attributeToUpdate = updatedAttributes.find((attribute) => attribute.field_name === attributeField);
    if (attributeToUpdate) {
        const uniqueValues = new Set();
        gridApi.forEachNode((rowNode) => {
            const columnValue = rowNode.data.fields?.[attributeToUpdate.field_name]?.string_value ?? "";
            if (columnValue) {
                uniqueValues.add(columnValue);
            }
        });

        attributeToUpdate.constraint = {
            ...(attributeToUpdate.constraint ?? {}),
            allowed_values: Array.from(uniqueValues).map((uniqueValue) => ({
                string_value: uniqueValue,
            })),
        };
    }

    return updatedAttributes;
};

interface ReduxProps {
    hasUnfinishedMutations: boolean;
}
interface IProps {
    allocationDimension: IAllocationDimension;
    readOnly?: boolean;
    allowNewRow?: boolean;
    allowSort?: boolean;
    onRowCreated?: (index: number, rowData: ICreateAllocationDimensionValue) => void;
    onRowUpdated?: (index: number, rowData: IUpdateAllocationDimensionValue) => void;
    onRowMove?: (index: number, destinationIndex: number) => void;
    onRowError?: (errorMessage: string) => void;
    onCellEditing?: (editMode: boolean) => void;
    hiddenColumns?: string[];
    uneditableColumns?: string[];
    purchaseOrderDoorGroups?: IPurchaseOrderDoorGroup[];
    unitsEntryType?: UnitsEntryType;
    onPurchaseOrdersError?: () => void;
    onActionInvoked?: (field_key: string, showDeleteDialog) => void;
    useLargeGrid?: boolean;
    onCustomDoorCellValueChanged?: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
    collectionsRefs?: Map<string, string>;
    onGridReady?: (e: GridReadyEvent) => void;
    onExportData?: (mode: ExportMode) => void;
}

export const AllocationDimensionGridComponent: React.FunctionComponent<IProps & ReduxProps> = ({
    allocationDimension,
    readOnly,
    allowNewRow = false,
    allowSort = false,
    onRowCreated,
    onRowUpdated,
    onRowMove,
    onRowError,
    onCellEditing,
    hiddenColumns = [],
    uneditableColumns = [],
    purchaseOrderDoorGroups,
    unitsEntryType,
    onPurchaseOrdersError,
    onActionInvoked,
    useLargeGrid,
    onCustomDoorCellValueChanged,
    collectionsRefs,
    onGridReady,
    hasUnfinishedMutations,
}) => {
    const [attributes, setLocalStateAttributes] = React.useState(allocationDimension.attributes.keys || []);

    React.useEffect(() => {
        // Check if there are ongoing mutations on the target allocation dimension
        // to prevent polution of the local state with wrong/partial data
        if (hasUnfinishedMutations) {
            return;
        }

        setLocalStateAttributes(allocationDimension.attributes.keys);
    }, [hasUnfinishedMutations, allocationDimension]);

    const [rootAttributes, userDefinedAttributes] = React.useMemo(() => {
        return [
            attributes.filter((attr) => !attr.display?.is_user_defined || !attr.collectionId),
            attributes.filter((attr) => attr.display?.is_user_defined && attr.collectionId),
        ];
    }, [attributes]);

    const hasPurchaseOrderDoorGroups = React.useMemo(
        () => rootAttributes.some((k: ISchemaKey) => k.field_name === PURCHASE_ORDER_DOOR_GROUP_FIELD),
        [rootAttributes]
    );

    const bulkPasteDef = React.useRef<{
        fieldNameToCustomValueList: any[];
        rowIndexToDataMap: Map<number, any>;
    }>(undefined);

    const columnDefs = React.useMemo<ColDef[]>(
        () => {
            const colDefs: ColDef[] = getCommonAllocationDimensionColDefs(readOnly, uneditableColumns);

            rootAttributes.forEach((attribute) => {
                const attributeColDef = buildAttributeColDef(
                    attribute,
                    {
                        readOnly,
                        purchaseOrderDoorGroups,
                    },
                    onActionInvoked
                );
                if (attributeColDef?.field === NUMBER_OF_DOORS && unitsEntryType !== UnitsEntryType.Average) {
                    hiddenColumns.push(attributeColDef.field);
                } else if (attributeColDef?.field === NUMBER_OF_DOORS) {
                    attributeColDef.cellEditorParams = {
                        currency: { decimal_places: 0 },
                    };
                }

                if (attributeColDef) {
                    colDefs.push(attributeColDef);
                }
            });

            const groupedAttributes = groupBy(userDefinedAttributes, "collectionId");
            Object.keys(groupedAttributes).forEach((collectionId) => {
                const customAttributes = groupedAttributes[collectionId];
                colDefs.push({
                    field: collectionId,
                    headerName: collectionsRefs?.get(collectionId) || collectionId,
                    editable: readOnly,
                    children: customAttributes.reduce<ColDef[]>((acc, attr) => {
                        const customAttributeDef = buildAttributeColDef(
                            attr,
                            {
                                readOnly,
                                purchaseOrderDoorGroups,
                                collectionId,
                            },
                            onActionInvoked
                        );
                        customAttributeDef && acc.push(customAttributeDef);
                        return acc;
                    }, []),
                } as ColGroupDef);
            });

            const filteredColDefs = colDefs.filter((colDef) => !hiddenColumns.includes(colDef.field));
            if (filteredColDefs.length > 0) {
                filteredColDefs[0].rowDrag = allowSort;
            }

            return filteredColDefs;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            rootAttributes,
            userDefinedAttributes,
            collectionsRefs,
            hiddenColumns,
            allowSort,
            purchaseOrderDoorGroups,
            readOnly,
            uneditableColumns,
        ]
    );

    React.useEffect(() => {
        if (onPurchaseOrdersError && hasPurchaseOrderDoorGroups && !purchaseOrderDoorGroups?.length) {
            onPurchaseOrdersError();
        }
    }, [onPurchaseOrdersError, hasPurchaseOrderDoorGroups, !!purchaseOrderDoorGroups?.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const verifyIfExternalIdIsDuplicated = React.useCallback(
        (api: GridApi, rowIndex: number, data: any) => {
            const isDuplicated = getRowDataWithIndex(api).some((row) => {
                const isEmptyExternalId = !data.external_id;
                const isSameRows = row.rowIndex === rowIndex;
                const isSameExternalId = row.data.external_id === data.external_id;
                return isSameExternalId && !isEmptyExternalId && !isSameRows;
            });
            if (isDuplicated && onRowError) {
                onRowError(
                    formatMessage("External id of |externalId| already exists.", { externalId: data.external_id })
                );
            }
            return isDuplicated;
        },
        [onRowError]
    );

    const handleCellChange = React.useCallback(
        (e: CellValueChangedEvent) => {
            const { oldValue, newValue, data, node, rowIndex, api, colDef } = e;

            if (oldValue === newValue) {
                api.hideOverlay();
                return;
            }
            const isExternalIdDuplicated = verifyIfExternalIdIsDuplicated(api, rowIndex, data);

            // Skip updating if row id is creation dummy id
            if (data.id === NEW_ROW_ID || isExternalIdDuplicated) {
                return;
            }

            setTimeout(() => {
                api.onFilterChanged();
            });

            // DoorGroupsSelectCellEditor is a custom component that will fire its own updates to BE.
            // This is because it supports both update dimension attributes and update dimension value requests which has to be done sequentially.
            if (colDef.cellEditor === Editors.DoorGroupsSelectCellEditor) {
                let shouldDeleteOldValueFromAllowedValues = false;
                const thisFieldExist = node.parent.allLeafChildren.some((row) =>
                    row.data?.fields.hasOwnProperty(colDef.field)
                );
                const isOldValueNotEmptyString = oldValue.trim() !== "";

                if (isOldValueNotEmptyString && thisFieldExist) {
                    shouldDeleteOldValueFromAllowedValues = node.parent.allLeafChildren.every(
                        (row) =>
                            row.data?.fields[colDef.field]?.string_value?.toLocaleLowerCase() !==
                            oldValue.toLocaleLowerCase()
                    );
                }

                let newValueFromCell: {
                    oldValue?: any;
                    shouldDeleteOldValueFromAllowedValues?: boolean;
                    field: string;
                    value: any;
                } = { field: colDef.field, value: newValue };

                if (shouldDeleteOldValueFromAllowedValues) {
                    newValueFromCell = {
                        ...newValueFromCell,
                        oldValue,
                        shouldDeleteOldValueFromAllowedValues,
                    };
                }

                // if this is a paste op, then capture the new values then process it within onPasteEnd callback.
                if (bulkPasteDef.current) {
                    // if this is paste operation (multi-cell), then add to the unmutated ref and dispatch during onPasteEnd.
                    const { fieldNameToCustomValueList, rowIndexToDataMap } = bulkPasteDef.current;

                    fieldNameToCustomValueList.push(newValueFromCell);
                    rowIndexToDataMap.set(rowIndex, data);

                    bulkPasteDef.current = { fieldNameToCustomValueList, rowIndexToDataMap };
                } else {
                    const updatedAttributes = updateAllowedValues(attributes, colDef.field, api);
                    onCustomDoorCellValueChanged(
                        updatedAttributes,
                        allocationDimension.id,
                        new Map<number, any>().set(rowIndex, data)
                    );
                    setLocalStateAttributes(updatedAttributes);
                }
            } else {
                onRowUpdated && onRowUpdated(node.rowIndex, data);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allocationDimension]
    );

    const handleCellPasteEnd = React.useCallback(
        (e: PasteEndEvent) => {
            if (
                bulkPasteDef.current &&
                (bulkPasteDef.current.fieldNameToCustomValueList.length || bulkPasteDef.current.rowIndexToDataMap.size)
            ) {
                const updatedAttributes = handleCustomDoorAttributeCellChanged({
                    fieldNameToCustomValueList: bulkPasteDef.current.fieldNameToCustomValueList,
                    rowIndexToDataMap: bulkPasteDef.current.rowIndexToDataMap,
                    isPasteOp: true,
                    attributes,
                    allocationDimensionId: allocationDimension.id,
                    onCustomDoorCellValueChanged,
                });
                setLocalStateAttributes(updatedAttributes);
                bulkPasteDef.current = undefined;
            } else {
                gridApi?.hideOverlay();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allocationDimension]
    );

    const [addingNewRow, setAddingNewRow] = React.useState<boolean>(false);
    const handleToggleNewRow = () => {
        setAddingNewRow((currentAddingNewRow) => !currentAddingNewRow);
    };
    const newRowDataRef = React.useRef<ICreateAllocationDimensionValue>(null);
    const handleSaveNewRow = () => {
        const newRow = gridApi.getRowNode(NEW_ROW_ID);
        newRowDataRef.current = newRow.data;

        if (!verifyIfExternalIdIsDuplicated(gridApi, newRow.rowIndex, newRow.data) && onRowCreated) {
            onRowCreated(newRow.rowIndex, {
                ...newRow.data,
                // BE won't save it, Just be safe, clean out id
                id: undefined,
            });
        }
    };

    const resetNewRowState = () => {
        setAddingNewRow(false);
        newRowDataRef.current = null;
    };

    const rowDragStartIndexRef = React.useRef<number | undefined>(undefined);
    const handleRowDragEnter = (e: RowDragEnterEvent) => {
        // Record initial index
        if (rowDragStartIndexRef.current === undefined) {
            rowDragStartIndexRef.current = e.node.rowIndex;
        }
    };
    const handleRowDragEnd = (e: RowDragEndEvent) => {
        if (e.node.data.id !== NEW_ROW_ID) {
            if (onRowMove) {
                onRowMove(rowDragStartIndexRef.current, e.node.rowIndex);
            }
        }
        rowDragStartIndexRef.current = undefined;
    };

    if (!allocationDimension) {
        return null;
    }

    const [gridApi, setGridApi] = React.useState<GridApi | null>(null); // eslint-disable-line react-hooks/rules-of-hooks
    const handleOnGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);

        // Auto-size columns width
        const allColumnIds = e.columnApi.getAllColumns().map((column) => column.getColId());
        e.columnApi.autoSizeColumns(allColumnIds);

        if (onGridReady) {
            onGridReady(e);
        }
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { rowData } = React.useMemo(() => {
        const rows = allocationDimension.dimension_values.concat([]);
        if (addingNewRow) {
            rows.push(cloneDeep(newRowData));
        }
        return { rowData: rows };
    }, [addingNewRow, allocationDimension.dimension_values]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
        if (!gridApi) {
            return;
        }
        gridApi.setRowData(rowData);

        // On receiving new data, check if it has newly added row. If it has, it's successfully created.
        if (
            newRowDataRef.current &&
            rowData.find(
                (row) =>
                    row[AllocationDimensionValueFields.ExternalId] ===
                    newRowDataRef.current[AllocationDimensionValueFields.ExternalId]
            )
        ) {
            resetNewRowState();
        }
    }, [rowData]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const handleCellEditingStarted = React.useCallback(() => {
        if (onCellEditing) {
            onCellEditing(true);
        }
    }, [onCellEditing]);

    return (
        <div {...(useLargeGrid ? largeGridStyles : styles)}>
            <div className="actions">
                {allowNewRow && (
                    <React.Fragment>
                        <FlatButton
                            label={!addingNewRow ? formatMessage("Add") : formatMessage("Cancel")}
                            onClick={handleToggleNewRow}
                        />
                        {addingNewRow && (
                            <FlatButton label={formatMessage("Save New Row")} onClick={handleSaveNewRow} />
                        )}
                    </React.Fragment>
                )}
            </div>
            <div
                className={`${theme.agGrid.defaultTheme} allocationDimensionGrid`}
                data-test={DATATEST.ASSORTMENTS.ALLOCATION_DIMENSIONS.GRID_WRAPPER}
            >
                <AgGridReact
                    onGridReady={handleOnGridReady}
                    defaultColDef={commonColDefProps}
                    immutableData={true}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    rowDragManaged={allowSort}
                    debug={false}
                    onRowDragEnter={handleRowDragEnter}
                    onRowDragEnd={handleRowDragEnd}
                    components={GRID_COMPONENTS_VANILLA}
                    frameworkComponents={GRID_COMPONENTS}
                    enableRangeSelection={true}
                    rowSelection="multiple"
                    enableCellChangeFlash={true}
                    animateRows={true}
                    suppressCopyRowsToClipboard={true}
                    stopEditingWhenCellsLoseFocus={true}
                    onCellValueChanged={handleCellChange}
                    getRowNodeId={getRowNodeId}
                    getContextMenuItems={getContextMenuItems}
                    onCellEditingStarted={handleCellEditingStarted}
                    onPasteStart={(e) => {
                        gridApi.showLoadingOverlay();
                        return (bulkPasteDef.current = {
                            fieldNameToCustomValueList: [],
                            rowIndexToDataMap: new Map<number, any>(),
                        });
                    }}
                    onPasteEnd={handleCellPasteEnd}
                    overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${formatMessage(
                        "Loading..."
                    )}</span>`}
                />
            </div>
        </div>
    );
};

/**
 * Check if value exists in allocationDimensionAttributes allowed_values and returns a mutated version if atleast one value is not in existing list.
 * If all values exist, then will return undefined. Used to fire updates to BE for updating new set of dimension_attributes.
 * @param allocationDimensionAttributes
 * @param fieldName
 * @param customValues
 * @returns undefined if customValue exists in allowed_values. Else returns mutated allocationDimensionAttributes.
 */
export const checkIfCustomValueInAllowedValues = (
    allocationDimensionAttributes: ISchemaKey[],
    fieldName: string,
    customValues: string[]
) => {
    const doorAttribute = allocationDimensionAttributes.find(
        (attribute: ISchemaKey) => attribute.field_name === fieldName
    );

    const allowedValues = doorAttribute?.constraint?.allowed_values ?? [];

    if (allowedValues) {
        const allowedValuesArray = allowedValues.map((item) => item.string_value?.toLocaleLowerCase());

        const sanitizedCustomValues = customValues.filter((item) => item.trim().length > 0); // ignore all empty strings.
        // find the diff between the two custom values array. Proceed only if there are differences
        const allowedValuesDiff = sanitizedCustomValues.filter(
            (customValue) => !allowedValuesArray.includes(customValue.toLocaleLowerCase())
        );

        if (allowedValuesDiff.length) {
            const newAllowedValues = allowedValues.concat(
                allowedValuesDiff.map((item) => {
                    return { string_value: item.trim() };
                })
            );
            const index = allocationDimensionAttributes.findIndex(
                (attribute: ISchemaKey) => attribute.field_name === fieldName
            );

            const newAllocationDimensionAttributes = replaceAllowedValueByIndex({
                allocationDimensionAttributes,
                allowedValues: newAllowedValues,
                doorAttribute,
                indexOfDoorAttribute: index,
            });

            return newAllocationDimensionAttributes;
        }
    }
    return allocationDimensionAttributes;
};

const mapStateToProps = ({ mutate }: IStore, { allocationDimension }: IProps) => {
    const hasUncommitedMutations = mutate.uncommittedMutations.getIn([allocationDimension.id])?.size;
    const hasSentMutations = mutate.sentMutations?.getIn([allocationDimension.id]);
    return {
        hasUnfinishedMutations: hasUncommitedMutations || hasSentMutations,
    };
};

export const AllocationDimensionGrid = connect<ReduxProps, unknown, IProps>(mapStateToProps)(
    AllocationDimensionGridComponent
);
